import React, { createContext, useCallback, useContext, useState, useEffect, type ReactNode } from 'react';
import { BASE_URL, refreshAccessToken } from '../api/client';
import {
  clearAccessToken, csrfHeaders, getAccessToken, purgeLegacyStoredToken,
  setAccessToken, subscribeToToken,
} from '../api/tokenStore';

/** The claims Ruby Form reads out of the access token. */
export interface JwtClaims {
  sub: string;
  role_id?: number;
  exp?: number;
}

export interface AuthUser {
  username: string;
  /** Lower is more privileged. 999 is the "no role claim" fallback. */
  role_id: number;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  token: string;
  /** True until the stored token has been decoded — routes must wait on this. */
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Decode the payload segment of a JWT. Returns null on anything malformed. */
function decodeClaims(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload)) as JwtClaims;
  } catch {
    return null;
  }
}

// Match Ruby Management's renewal profile: renew while the current access
// token still has ten minutes left, leaving room for a transient network blip.
export const REFRESH_EARLY_MS = 10 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 1000;

export function millisecondsUntilRefresh(token: string, now = Date.now()): number | null {
  const exp = decodeClaims(token)?.exp;
  if (!exp) return null;
  return Math.max(MIN_REFRESH_DELAY_MS, exp * 1000 - now - REFRESH_EARLY_MS);
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string>(() => getAccessToken() || '');
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((nextToken: string | null) => {
    if (!nextToken) {
      setToken(''); setUser(null); setIsAuthenticated(false); return;
    }
    const claims = decodeClaims(nextToken);
    if (!claims) {
      clearAccessToken(); setToken(''); setUser(null); setIsAuthenticated(false); return;
    }
    setToken(nextToken);
    setUser({ username: claims.sub, role_id: claims.role_id ?? 999 });
    setIsAuthenticated(true);
  }, []);

  useEffect(() => subscribeToToken(applyToken), [applyToken]);

  useEffect(() => {
    let cancelled = false;
    const initAuth = async () => {
      purgeLegacyStoredToken();
      const refreshed = await refreshAccessToken();
      if (!cancelled && refreshed) applyToken(refreshed);
      if (!cancelled) setLoading(false);
    };
    initAuth();
    return () => { cancelled = true; };
  }, [applyToken]);

  useEffect(() => {
    if (!token) return undefined;
    const delay = millisecondsUntilRefresh(token);
    if (delay === null) return undefined;

    const timer = window.setTimeout(async () => {
      // A failed proactive renewal must not erase a still-usable session. The
      // normal request path will retry, and only a rejected cookie ends it.
      await refreshAccessToken().catch(() => undefined);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [token]);

  const login = async (username: string, password: string): Promise<void> => {
    const res = await fetch(`${BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // store HttpOnly refresh_token cookie
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      if (res.status === 429) throw new Error('Too many failed attempts. Try again in a few minutes.');
      throw new Error('Login failed');
    }

    const data = (await res.json()) as { access_token: string };
    setAccessToken(data.access_token);
  };

  const logout = async (): Promise<void> => {
    try {
      await fetch(`${BASE_URL}/api/auth/logout`, {
        method: 'POST', credentials: 'include', headers: { ...csrfHeaders() },
      });
    } catch { /* best effort */ }
    clearAccessToken();
    applyToken(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, token, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
